import { updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { getAuth } from 'firebase/auth';
import { useCallback } from 'react';

import type { RecentTarget } from '~/components/SuperadminSwitch/switchPalette.model';
import { useAuthContext } from '~/auth/auth.context';
import { parseRecents, pushRecent } from '~/components/SuperadminSwitch/switchPalette.model';
import { api } from '~/utils/api';

/** Recents are per superadmin: keyed on the signed-in Firebase uid, which stays the superadmin's while impersonating. */
const recentsKey = () => `superadmin-switch-recents:${getAuth().currentUser?.uid ?? 'anon'}`;
export const readRecents = (): RecentTarget[] => {
  try {
    return parseRecents(window.localStorage.getItem(recentsKey()));
  } catch {
    return [];
  }
};
const writeRecents = (list: RecentTarget[]) => {
  try {
    window.localStorage.setItem(recentsKey(), JSON.stringify(list));
  } catch {
    // Private windows / blocked storage: recents are a convenience, never a blocker.
  }
};

/**
 * Become someone else (superadmins only) — the one switch behind the ⌘⇧K palette, the superadmin
 * bar's Stop and the campaign page's "Switch to <owner>". Remembers them in the palette's recents.
 * Where you land afterwards is the page's business: a campaign page leaves for the list when it isn't
 * the new person's (useLeaveCampaignOnIdentityChange), the same for a switch made in another tab.
 *
 * Resolves true once switched; false when the switch failed (the toast already said why).
 */
export function useSwitchToUser() {
  const [auth, { startImpersonation, stopImpersonation }] = useAuthContext();
  const impersonateMutation = api.superAdmin.impersonate.useMutation();

  /** Stop impersonating: back to acting as yourself. */
  const switchBack = stopImpersonation;

  const switchTo = useCallback(
    async (target: RecentTarget): Promise<boolean> => {
      // Yourself: the server refuses to impersonate the signed-in superadmin, so that's a switch back.
      if (target.userId === getAuth().currentUser?.uid) {
        stopImpersonation();
        return true;
      }
      try {
        const data = await updateWithToastPromise({
          loading: `Switching to ${target.name}…`,
          success: `Now acting as ${target.name}`,
          mutationFnAsync: () => impersonateMutation.mutateAsync({ userId: target.userId }),
        });
        writeRecents(pushRecent(readRecents(), target));
        startImpersonation(
          data.impersonationToken,
          target.email,
          target.userId,
          { name: data.superadminName, teamName: data.superadminTeamName },
          target.profilePicture,
        );
        return true;
      } catch {
        return false;
      }
    },
    [impersonateMutation, startImpersonation, stopImpersonation],
  );

  const isSuperAdmin = auth.impersonation != null || auth.user?.isSuperAdmin === true;
  return { switchTo, switchBack, isSwitching: impersonateMutation.isPending, isSuperAdmin };
}
